import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  PopoverCloseButton,
  Select,
  Textarea,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { FiEdit2, FiRotateCcw } from 'react-icons/fi';

/**
 * Muestra y edita el "inicio de cobro" (ancla) para semester-based con una sola
 * matrícula. Vive en la franja "SE VINCULARÁ A" del ContextBanner, al extremo
 * derecho. Selector de mes/año + nota obligatoria (>=10 caracteres).
 *
 * Props:
 *  - enrollmentType, enrollmentId
 *  - override: string ISO 'YYYY-MM-DD' | null   (financialAnchorOverride)
 *  - overrideNote, overrideAt, overrideBy       (para el pie del popover)
 *  - anchorAuto: string ISO | null              (fecha calculada automática cuando no hay override)
 *  - onChange({ date, note }): callback tras guardar (para refetch)
 *  - onError(message)
 *  - setAnchorOverride: función del hook useLinkPaymentsData
 */
export default function AnchorOverrideControl({
  enrollmentType,
  enrollmentId,
  override,
  overrideNote,
  overrideAt,
  overrideBy,
  anchorAuto,
  onChange,
  setAnchorOverride,
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null);
  const [note, setNote] = useState('');

  const displayDate = override || anchorAuto || null;
  const isManual = !!override;

  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    const arr = [];
    for (let y = now + 1; y >= now - 6; y--) arr.push(y);
    return arr;
  }, []);
  const monthOptions = [
    { v: 1, n: 'Enero' }, { v: 2, n: 'Febrero' }, { v: 3, n: 'Marzo' },
    { v: 4, n: 'Abril' }, { v: 5, n: 'Mayo' }, { v: 6, n: 'Junio' },
    { v: 7, n: 'Julio' }, { v: 8, n: 'Agosto' }, { v: 9, n: 'Septiembre' },
    { v: 10, n: 'Octubre' }, { v: 11, n: 'Noviembre' }, { v: 12, n: 'Diciembre' },
  ];

  useEffect(() => {
    if (!isOpen) return;
    const base = override ? new Date(override) : (anchorAuto ? new Date(anchorAuto) : new Date());
    setYear(base.getUTCFullYear());
    setMonth(base.getUTCMonth() + 1);
    setNote(overrideNote || '');
  }, [isOpen, override, anchorAuto, overrideNote]);

  const canSave = year && month && note.trim().length >= 10;

  const handleSave = async () => {
    if (!canSave || !enrollmentId || !enrollmentType) return;
    setSaving(true);
    const date = `${year}-${String(month).padStart(2, '0')}-01`;
    try {
      await setAnchorOverride({ enrollmentType, enrollmentId, date, note: note.trim() });
      toast({ title: 'Inicio de cobro actualizado', status: 'success', position: 'bottom' });
      onClose();
      if (onChange) onChange({ date, note: note.trim() });
    } catch (err) {
      const msg = err?.response?.data?.error?.message || 'No se pudo guardar';
      toast({ title: msg, status: 'error', position: 'bottom' });
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!enrollmentId || !enrollmentType) return;
    setSaving(true);
    try {
      await setAnchorOverride({ enrollmentType, enrollmentId, date: null });
      toast({ title: 'Inicio de cobro vuelve al automático', status: 'success', position: 'bottom' });
      onClose();
      if (onChange) onChange({ date: null, note: null });
    } catch (err) {
      const msg = err?.response?.data?.error?.message || 'No se pudo limpiar';
      toast({ title: msg, status: 'error', position: 'bottom' });
    } finally {
      setSaving(false);
    }
  };

  const dateFmt = (iso) => {
    if (!iso) return '—';
    const [y, m] = String(iso).split('-');
    const name = monthOptions[Number(m) - 1]?.n || m;
    return `${name} ${y}`;
  };

  return (
    <Box textAlign="right" minW="150px">
      <Text fontSize="10px" fontWeight="700" color="#5B21B6" letterSpacing="0.04em" textTransform="uppercase">
        Inicio de cobro
      </Text>
      <Flex align="baseline" justify="flex-end" gap={1.5} mt={0.5}>
        <Text fontSize="14px" fontWeight="700" color="gray.800" fontVariantNumeric="tabular-nums">
          {dateFmt(displayDate)}
        </Text>
        <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-end">
          <PopoverTrigger>
            <Button
              size="xs"
              variant="ghost"
              p={1}
              minW="auto"
              h="auto"
              color="#5B21B6"
              _hover={{ bg: '#EDE9FE' }}
              aria-label="Editar inicio de cobro"
            >
              <FiEdit2 size={12} />
            </Button>
          </PopoverTrigger>
          <PopoverContent w="320px">
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverHeader fontSize="13px" fontWeight="600">Editar inicio de cobro</PopoverHeader>
            <PopoverBody>
              <Flex gap={2} mb={3}>
                <Box flex="1">
                  <Text fontSize="11px" color="gray.600" mb={1}>Mes</Text>
                  <Select size="sm" value={month || ''} onChange={(e) => setMonth(Number(e.target.value))}>
                    <option value="">—</option>
                    {monthOptions.map((m) => <option key={m.v} value={m.v}>{m.n}</option>)}
                  </Select>
                </Box>
                <Box flex="1">
                  <Text fontSize="11px" color="gray.600" mb={1}>Año</Text>
                  <Select size="sm" value={year || ''} onChange={(e) => setYear(Number(e.target.value))}>
                    <option value="">—</option>
                    {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                  </Select>
                </Box>
              </Flex>
              <Box>
                <Text fontSize="11px" color="gray.600" mb={1}>Razón del ajuste *</Text>
                <Textarea
                  size="sm"
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Ej. pasó a convalidación en marzo 2026"
                />
                <Text fontSize="10px" color={note.trim().length >= 10 ? 'gray.500' : 'orange.600'} mt={1}>
                  Mínimo 10 caracteres · queda registrado en auditoría
                </Text>
              </Box>
              {isManual && (
                <Box mt={3} p={2} bg="#F5F3FF" borderRadius="4px">
                  <Text fontSize="11px" color="gray.700">
                    <b>Actualmente ajustado</b>
                    {overrideBy != null && <> por usuario #{overrideBy}</>}
                    {overrideAt && <> · {new Date(overrideAt).toLocaleDateString('es-CO')}</>}
                  </Text>
                </Box>
              )}
            </PopoverBody>
            <PopoverFooter>
              <Flex justify="space-between" gap={2}>
                {isManual ? (
                  <Button
                    size="xs"
                    variant="ghost"
                    leftIcon={<FiRotateCcw />}
                    onClick={handleClear}
                    isLoading={saving}
                    isDisabled={saving}
                  >
                    Volver al automático
                  </Button>
                ) : <Box />}
                <Button
                  size="xs"
                  bg="#574feb"
                  color="white"
                  _hover={{ bg: '#4437c9' }}
                  onClick={handleSave}
                  isLoading={saving}
                  isDisabled={!canSave || saving}
                >
                  Guardar
                </Button>
              </Flex>
            </PopoverFooter>
          </PopoverContent>
        </Popover>
      </Flex>
      <Text fontSize="10px" color={isManual ? '#5B21B6' : 'gray.500'} fontStyle="italic" mt={0.5}>
        {isManual ? 'ajustado manualmente' : 'automático'}
      </Text>
    </Box>
  );
}
